import { Stack } from "@mui/material";
import { parseISO } from "date-fns";
import { useState } from "react";
import { numberComparerDescBy } from "@entrilia/shared/utilities/arrayHelper";
import type { Note } from "../../api/types/notesTypes";
import AddNoteButton from "./AddNoteButton";
import DeleteNoteDialog from "./DeleteNoteDialog";
import NoNotes from "./NoNotes";
import NoteCard from "./NoteCard";
import NoteEditor from "./NoteEditor";
import { useNotesContext } from "./NotesContext";

interface Props {
  notes: Note[];
  onCreated: (note: Note) => void;
  onUpdated: (note: Note) => void;
  onDeleted: (noteId: string) => void;
}

interface State {
  addingNote: boolean;
  editingNoteId?: string;
  deletingNoteId?: string;
}

const sortNotes = (notes: Note[]) =>
  [...notes].sort(numberComparerDescBy((note) => parseISO(note.updatedAt).getTime()));

const NotesList = ({ notes, onCreated, onUpdated, onDeleted }: Props) => {
  const { hasPermissionsToManageNotes } = useNotesContext();

  const [state, setState] = useState<State>({ addingNote: false });

  const handleAddNote = () => {
    setState((prev) => ({ ...prev, addingNote: true, editingNoteId: undefined }));
  };

  const handleEditNote = (noteId: string) => {
    setState((prev) => ({ ...prev, editingNoteId: noteId, addingNote: false }));
  };

  const handleCancelEditingNote = () => {
    setState((prev) => ({ ...prev, editingNoteId: undefined, addingNote: false }));
  };

  const handleDeleteNote = (noteId: string) => {
    setState((prev) => ({ ...prev, deletingNoteId: noteId }));
  };

  const handleNoteSaved = (noteId: string | undefined, note: Note) => {
    const isNewNote = !noteId;
    if (isNewNote) {
      onCreated(note);
    } else {
      onUpdated(note);
    }

    setState((prev) => ({ ...prev, editingNoteId: undefined, addingNote: false }));
  };

  const handleNoteDeleted = (noteId: string) => {
    onDeleted(noteId);
    setState((prev) => ({ ...prev, deletingNoteId: undefined }));
  };

  const handleCancelDeletingNote = () => {
    setState((prev) => ({ ...prev, deletingNoteId: undefined }));
  };

  const sortedNotes = sortNotes(notes);

  return (
    <Stack spacing={1.5} sx={{ width: "100%", px: 2.5, pb: 2 }}>
      {hasPermissionsToManageNotes && !state.addingNote && <AddNoteButton onClick={handleAddNote} />}
      {sortedNotes.length === 0 && !state.addingNote && <NoNotes />}
      {state.addingNote && (
        <NoteEditor initialContent="" onSaved={handleNoteSaved} onCancel={handleCancelEditingNote} />
      )}
      {sortedNotes.map((note) =>
        state.editingNoteId === note.id ? (
          <NoteEditor
            key={note.id}
            id={note.id}
            initialContent={note.htmlContent}
            onSaved={handleNoteSaved}
            onCancel={handleCancelEditingNote}
          />
        ) : (
          <NoteCard key={note.id} note={note} onEdit={handleEditNote} onDelete={handleDeleteNote} />
        )
      )}
      <DeleteNoteDialog
        noteId={state.deletingNoteId}
        onCancel={handleCancelDeletingNote}
        onDeleted={handleNoteDeleted}
      />
    </Stack>
  );
};

export default NotesList;
